import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { OrderModel } from "api";
import { useOrderService } from "contexts/ServiceContext";
import { CONTRACTS_QUERY_KEY } from "hooks/api/contracts/useContracts";
import {
  AI_EVENTS,
  EventTrackFunction,
  useTrackEvent,
} from "../../useAppInsight";
import { ApiStatusException } from "services/apiErrors";

export const ORDERS_QUERY_KEY = "orders";

const handleTrackError = (
  trackQueryError: EventTrackFunction,
  eventId: string,
  err: ApiStatusException
) => {
  trackQueryError(
    {
      statusCode: err.statusCode as number,
      message: err.message,
    },
    eventId
  );
  throw err;
};

export const useOrderAddresses = (
  contractNumber: number | undefined,
  shipFrom: string | undefined,
  mode: string | undefined,
  salesTerms: string | undefined
): UseQueryResult<CustomerAddressCode[]> => {
  const orderService = useOrderService();
  const trackQuery = useTrackEvent(AI_EVENTS.API_REQUEST, {
    contractNumber,
  });
  const trackQueryError = useTrackEvent(AI_EVENTS.API_ERROR, {
    contractNumber,
  });

  const enabled = contractNumber && shipFrom && mode && salesTerms;
  return useQuery({
    queryKey: [
      ORDERS_QUERY_KEY,
      {
        contractNumber,
        warehouseCode: shipFrom,
        modeOfTransportationCode: mode,
        salesTermsCode: salesTerms,
      },
      "get-valid-shipTos",
    ],
    queryFn: () => {
      const eventId = trackQuery({
        endpoint: "Order",
      });
      return enabled
        ? orderService
            .getValidDestinations(contractNumber, {
              warehouseCode: shipFrom,
              modeOfTransportationCode: mode,
              salesTermsCode: salesTerms,
            })
            .catch((err) => handleTrackError(trackQueryError, eventId, err))
        : Promise.reject();
    },
    enabled: !!enabled,
    select: (data) => data.value,
    keepPreviousData: true,
  });
};

export const ordersQueryKeyFactory = (
  contractNumber: number | undefined,
  filters?: Record<string, unknown>
) =>
  filters
    ? [CONTRACTS_QUERY_KEY, ORDERS_QUERY_KEY, { contractNumber }, filters]
    : [CONTRACTS_QUERY_KEY, ORDERS_QUERY_KEY, { contractNumber }];

const useOrders = (
  contractNumber: number | undefined,
  searchTerm: string,
  filters?: Partial<OrdersFilters>,
  currentSort?: { key: keyof OrderModel; direction: string }
): UseQueryResult<OrderLineItem[]> => {
  const orderService = useOrderService();
  const trackQuery = useTrackEvent(AI_EVENTS.API_REQUEST, {
    contractNumber,
  });
  const trackQueryError = useTrackEvent(AI_EVENTS.API_ERROR, {
    contractNumber,
  });

  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ordersQueryKeyFactory(contractNumber, {
      searchTerm,
      filters,
      currentSort,
    }),
    queryFn: () => {
      const eventId = trackQuery({
        endpoint: "Order",
        parameters: { filters, sorting: currentSort, searchTerm },
      });
      return contractNumber
        ? orderService
            .listOrders(
              contractNumber,
              searchTerm,
              {
                shipFrom: filters?.shipFrom ?? [],
                shipTo: filters?.shipTo ?? [],
                mode: filters?.mode ?? [],
              },
              currentSort
            )
            .catch((err) => handleTrackError(trackQueryError, eventId, err))
        : Promise.reject();
    },
    enabled: !!contractNumber,
    select: (data) => data.value,
    keepPreviousData: true,
  });
};

export default useOrders;
